import React, {useContext, useEffect, useRef, useCallback, useState} from 'react';
import {Badge, Button, Card, Pagination, Spinner, Table} from "react-bootstrap";
import {Context} from "../../index";
import {useNavigate} from "react-router-dom";
import {fetchUserFinLogs, markFinLogsAsRead} from "../../http/userLogAPI";
import {ADD_FUNDS_TO_USD_BALANCE_ROUTE} from "../../utils/consts";

const UsdUserLogsInDashboard = ({ refreshKey = 0 }) => {
    const { user } = useContext(Context);
    const [logs, setLogs] = useState([]);
    const [logsCount, setLogsCount] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [loading, setLoading] = useState(true);
    const [markingRead, setMarkingRead] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const navigate = useNavigate();
    const totalPages = Math.max(1, Math.ceil(logsCount / limit));
    const secondaryActivePageStyle = {
        backgroundColor: "#6c757d",
        borderColor: "#6c757d",
        color: "#fff",
    };

    // раскрытие строк / карточек
    const [openRows, setOpenRows] = useState(new Set());

    const isOpen = (id) => openRows.has(id);

    const loadLogs = useCallback(async ({ showMainLoader = false } = {}) => {
        if (!user?.user?.id) return;
        if (showMainLoader) setLoading(true);

        const data = await fetchUserFinLogs(user.user.id, { page, limit });
        const items = Array.isArray(data) ? data : (data?.items || []);

        setLogs(items);
        setLogsCount(Number(Array.isArray(data) ? data.length : data?.count || 0));
        setOpenRows(new Set());
        if (showMainLoader) setLoading(false);
    }, [user?.user?.id, page, limit]);

    useEffect(() => {
        if (user.activeComponent === "UsdBalanceLogs") {
            loadLogs({ showMainLoader: true }).catch((error) => {
                console.error("Error fetching USD logs:", error);
                setLoading(false);
            });
        }
    }, [user.user.id, user.activeComponent, refreshKey, page, loadLogs]);

    const handleMarkAsRead = useCallback(async () => {
        if (markingRead) return;
        setMarkingRead(true);
        try {
            await markFinLogsAsRead(user.user.id);           // без второго аргумента
            setLogs(prev => prev.map(log => ({ ...log, is_read: true })));
            user.setReadCountUsd(0);
        } finally {
            setMarkingRead(false);
        }
    }, [markingRead, user.user.id, user.setReadCountUsd]);


// refs
    const rowTimersRef = useRef(new Map()); // Map<logId, timeoutId>

// поставить таймер на 5с для конкретного лога
    const scheduleMarkRowRead = useCallback((log) => {
        if (log.is_read) return;
        if (rowTimersRef.current.has(log.id)) return;

        const tid = setTimeout(async () => {
            try {
                await markFinLogsAsRead(user.user.id, log.id); // ← один лог
                setLogs(prev => prev.map(l => (l.id === log.id ? { ...l, is_read: true } : l)));
                user.setReadCountUsd?.(Math.max(0, (user.readCountUsd || 0) - 1));
            } finally {
                rowTimersRef.current.delete(log.id);
            }
        }, 2000);

        rowTimersRef.current.set(log.id, tid);
    }, [user.user.id, user]);

    const clearRowTimer = useCallback((logId) => {
        const tid = rowTimersRef.current.get(logId);
        if (tid) {
            clearTimeout(tid);
            rowTimersRef.current.delete(logId);
        }
    }, []);

    useEffect(() => () => {
        rowTimersRef.current.forEach(tid => clearTimeout(tid));
        rowTimersRef.current.clear();
    }, []);


    const updateUserDataState = async () => {
        try {
            setRefreshing(true);
            await loadLogs();
        } finally {
            setRefreshing(false);
        }
    };

    const toggleRow = (id) =>
          setOpenRows((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                      next.delete(id);
                      clearRowTimer(id);
                    } else {
                      next.add(id);
                      const log = logs.find(l => l.id === id);
                      if (log) scheduleMarkRowRead(log);
                    }
                return next;
              });

    return (
        <Card className="p-3 w-100 mt-0">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <h5 className="text-muted mb-2 mb-md-0">Usd Balance Logs</h5>

                <div className="d-flex flex-row flex-md-row align-items-start ms-md-auto flex-wrap gap-2 gap-md-2">
                    <Button
                        variant="success"
                        onClick={() => navigate(ADD_FUNDS_TO_USD_BALANCE_ROUTE)}
                        className="btn-sm"
                    >
                        Add Funds
                    </Button>

                    <Button
                        variant="outline-secondary"
                        onClick={updateUserDataState}
                        className="btn-sm"
                        disabled={refreshing}
                    >
                        {refreshing ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        ) : null}
                        Refresh Data
                    </Button>

                    {logs.length > 0 && (
                        <Button
                            variant="outline-success"
                            onClick={handleMarkAsRead}
                            disabled={markingRead}
                            className="btn-sm"
                        >
                            {markingRead ? (
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                            ) : null}
                            Read All
                        </Button>
                    )}
                </div>
            </div>

            <div className="text-muted small mt-2">
                <p className="mb-0">
                    This section displays your full history of USD balance changes — including earnings, bonuses, and deductions.
                </p>
                <p className="mb-0">
                    Each entry gives you a transparent view of what affected your balance, when it happened, and why.
                </p>
                <p className="mb-3">
                    <b>Note:</b> this is a payments-only balance. Payouts/withdrawals or transfers from this balance are not supported.
                </p>
            </div>

            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" />
                </div>
            ) : logs.length > 0 ? (
                <>
                    {/* 📱 Mobile cards (xs–sm): label — value в одну строку, space-between */}
                    <div className="d-md-none">
                        {logs.map((log) => {
                            const amount =
                                Number(log.subtotal_amount) === 0
                                    ? 'Subscr'
                                    : `${log.subtotal_amount > 0 ? '+' : log.subtotal_amount < 0 ? '-' : ''}$${Math.abs(log.subtotal_amount).toFixed(2)}`;
                            const balance = `$${Number(log.lr_balance_state).toFixed(2)}`;
                            const dateStr = new Date(log.log_date).toLocaleDateString('en-GB', {
                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
                            });

                            return (
                                <div
                                    key={log.id}
                                    className={`card shadow-sm mb-2 ${isOpen(log.id) ? 'border-success' : ''}`}
                                    role="button"
                                    aria-expanded={isOpen(log.id)}
                                    onClick={() => toggleRow(log.id)}
                                >
                                    <div className="card-body py-2">

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">
                                                {isOpen(log.id) ? '▾ ' : '▸ '}Amount
                                            </small>
                                            <span className=" text-end small text-truncate" style={{maxWidth: '65%'}}>{amount}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Balance</small>
                                            <span className=" text-end small text-truncate" style={{maxWidth: '65%'}}>{balance}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Description</small>
                                            <span className="text-end small text-truncate" style={{maxWidth: '65%'}}>{log.description || '—'}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Date</small>
                                            <span className="text-end small text-truncate" style={{maxWidth: '65%'}}>{dateStr}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Status</small>
                                            <span className="text-end small">
                        {!log.is_read ? <Badge bg="success">New</Badge> : <span className="text-muted">Read</span>}
                      </span>
                                        </div>

                                        {isOpen(log.id) && (
                                            <div className="mt-2 p-2 rounded-2 border border-1 border-secondary-subtle bg-light">
                                                <div className="text-muted small mb-1">Full text of this log:</div>
                                                <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
                                                    {log.description || "—"} {log.user_stripe_invoice_pdf || "No PDF"}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* 💻 Desktop table (md+): кликабельные строки */}
                    <div className="d-none d-md-block" style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '8px' }}>
                        <Table bordered hover size="sm" striped className="mt-1" style={{ minWidth: '600px' }}>
                            <thead>
                            <tr className="bg-secondary text-center bg-opacity-50 text-sm">
                                <th className="small text-muted">Amount:</th>
                                <th className="small text-muted">Balance:</th>
                                <th className="small text-muted">Description:</th>
                                <th className="small text-muted">Date:</th>
                                <th className="small text-muted">Invoice:</th>
                                <th className="small text-muted">Status:</th>
                            </tr>
                            </thead>
                            <tbody>
                            {logs.map((log) => (
                                <React.Fragment key={log.id}>
                                    <tr
                                        onClick={() => toggleRow(log.id)}
                                        style={{ cursor: "pointer" }}
                                        className={isOpen(log.id) ? "table-active small fst-italic" : "small fst-italic"}
                                        role="button"
                                        aria-expanded={isOpen(log.id)}
                                    >
                                        <td className="text-center small text-success fw-bold" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {isOpen(log.id) ? "▾ " : "▸ "}
                                            {Number(log.subtotal_amount) === 0
                                                ? 'Subscr'
                                                : `${(log.subtotal_amount > 0 && log.log_type !== 'subscription_renewal_paid') ? '+' : log.subtotal_amount < 0 ? '-' : ''}$${Math.abs(log.subtotal_amount).toFixed(2)}`}
                                        </td>

                                        <td className="text-center small" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            ${Number(log.lr_balance_state).toFixed(2)}
                                        </td>

                                        <td className="small text-muted" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "600px", width: "600px" }}>
                                            {log.description || "—"}
                                        </td>

                                        <td className="text-center small text-muted" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {new Date(log.log_date).toLocaleDateString('en-GB', {
                                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
                                            })}
                                        </td>
                                        <td
                                            className="small text-center align-middle p-0"
                                            style={{ maxWidth: "50px", width: "50px" }}
                                        >
                                            {log.user_stripe_invoice_pdf ? (
                                                <a
                                                    href={log.user_stripe_invoice_pdf}
                                                    download
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="d-inline-flex justify-content-center align-items-center text-success"
                                                    title="Download invoice PDF"
                                                    aria-label="Download invoice PDF"
                                                    style={{ width: 24, height: 24 }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M14 2H7a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V9z" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                                        <path d="M14 2v7h7" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                                        <text x="6.8" y="17" fontSize="7" fontWeight="700" fill="currentColor">PDF</text>
                                                    </svg>
                                                </a>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>




                                        <td className="text-center align-items-center small" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {!log.is_read ? <Badge bg="success">New</Badge> : <span className="text-muted">Read</span>}
                                        </td>
                                    </tr>


                                    {isOpen(log.id) && (
                                        <tr className="small">
                                            <td colSpan={6} className="text-start py-2">
                                                <div className="p-2 rounded-2 border border-1 border-secondary-subtle bg-light">
                                                    <div className="text-muted fw-semibold mb-1">Full text of this log:</div>
                                                    <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                                                        {log.description || "—"}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            </tbody>
                        </Table>
                    </div>

                    {logsCount > limit && (
                        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2 mt-2">
                            <div className="small text-muted">
                                Showing {(page - 1) * limit + 1}-{Math.min(page * limit, logsCount)} of {logsCount} logs
                            </div>
                            <Pagination size="sm" className="mb-0">
                                <Pagination.First
                                    disabled={page <= 1 || loading || refreshing}
                                    onClick={() => setPage(1)}
                                />
                                <Pagination.Prev
                                    disabled={page <= 1 || loading || refreshing}
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                />
                                <Pagination.Item active linkStyle={secondaryActivePageStyle}>
                                    {page} / {totalPages}
                                </Pagination.Item>
                                <Pagination.Next
                                    disabled={page >= totalPages || loading || refreshing}
                                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                />
                                <Pagination.Last
                                    disabled={page >= totalPages || loading || refreshing}
                                    onClick={() => setPage(totalPages)}
                                />
                            </Pagination>
                        </div>
                    )}
                </>
            ) : (
                <p className="text-center text-muted">No Financial Logs</p>
            )}
        </Card>
    );
};

export default UsdUserLogsInDashboard;
